import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import useHeldGatewayOnboardings from '../../hooks/useHeldGatewayOnboardings';

const waitingSince = created => {
    const at = DateTime.fromISO(created);
    return at.isValid ? at.toRelative() : '';
};

// Gateways onboarded before they had ever connected. Each completes by itself once the
// gateway comes online and then shows up in "All gateways" below; until then it only
// exists here. A collapsible panel like the capacity one above it: collapsed, the
// header keeps the count and the oldest wait in view; open, the table scrolls in place.
// Rendered only when something is waiting.
const HeldGatewayOnboardings = ({ reloadToken }) => {
    const { isReady, isError, data } = useHeldGatewayOnboardings(reloadToken);
    const [isOpen, setIsOpen] = useState(false);

    if (!isReady || isError || !data.length) {
        return null;
    }

    // Newest first from the api, so the oldest wait is the last row.
    const oldest = waitingSince(data[data.length - 1].created);

    return (
        <section className="gateways-overview__areas gateways-overview__panel gateways-overview__panel--held" data-testid="held-gateway-onboardings">
            <h2 className="gateways-overview__areas-header">
                <span className="gateways-overview__areas-heading">
                    <button type="button" className="gateways-overview__areas-toggle" aria-expanded={isOpen} onClick={() => setIsOpen(open => !open)}>
                        <span className="gateways-overview__areas-caret" aria-hidden="true">
                            {isOpen ? '▾' : '▸'}
                        </span>
                        Awaiting connection
                        <span className="gateways-overview__held-count">{data.length}</span>
                    </button>
                    <span className="gateways-overview__areas-summary">
                        {data.length} waiting{oldest ? ` · oldest since ${oldest}` : ''} · each completes automatically once its gateway connects,
                        then appears under All gateways
                    </span>
                </span>
            </h2>

            {isOpen ? (
                <div className="gateways-overview__panel-body gateways-overview__held-scroll">
                    <table className="gateways-overview__table gateways-overview__held-table">
                        <thead>
                            <tr>
                                <th>Serial</th>
                                <th>Customer</th>
                                <th>Address</th>
                                <th>Inverter brand</th>
                                <th>Price area</th>
                                <th>Waiting since</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(item => (
                                <tr key={item.serial_number} data-testid="held-gateway-onboarding">
                                    <td className="gateways-overview__mono">
                                        <span className="gateway-section__waiting-badge">
                                            <i className="fa-regular fa-clock"></i>
                                            <span>{item.serial_number}</span>
                                        </span>
                                    </td>
                                    <td>
                                        {item.retailer_customer_id ? (
                                            <Link
                                                className="gateways-overview__held-link"
                                                to={`/devicesdetail/${encodeURIComponent(item.retailer_customer_id)}`}
                                            >
                                                {item.retailer_customer_id}
                                            </Link>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    <td>{item.address || '—'}</td>
                                    <td>{item.inverter_brand}</td>
                                    <td>{item.price_area}</td>
                                    <td title={item.created}>{waitingSince(item.created)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </section>
    );
};

export default HeldGatewayOnboardings;
